import { useState } from "react";
import { ChevronDown, ChevronRight, Minus, Plus } from "lucide-react";



function Chip({ active, children, onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest border border-solid
        border-collectors-black transition-colors ${
          active
            ? "bg-collectors-black text-white"
            : "bg-transparent text-collectors-gray hover:text-collectors-black"
        }`}
    >
      {children}
    </button>
  );
}

/**
 * Column visibility for a grouped table, plus — where the table has one — how
 * many products each row shows.
 *
 * Collapsed by default: sixteen toggles above the table would compete with the
 * table for attention, and most sessions never touch them.
 *
 * `groups` is passed in rather than imported so the performance and product
 * tables can share this without either one owning the other's columns. The
 * products stepper only appears when `onProductCount` is supplied.
 */
export default function ColumnPicker({
  groups,
  hidden,
  onToggleColumn,
  onShowAll,
  onHideAll,
  productCount,
  maxProducts,
  onProductCount,
}) {
  const [open, setOpen] = useState(false);
  const hiddenCount = hidden.size;
  const hideableCount = groups.reduce(
    (n, g) => n + g.columns.filter((c) => !c.locked).length,
    0
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 px-4 py-2 font-mono text-xs uppercase tracking-widest
            border border-solid border-collectors-black hover:bg-collectors-black
            hover:text-white transition-colors"
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          Columns
          {hiddenCount > 0 && <span className="normal-case">({hiddenCount} hidden)</span>}
        </button>

        {onProductCount && (
        <div className="flex items-center gap-2">
          <span className="font-mono text-[11px] uppercase tracking-widest text-collectors-gray">
            Products shown
          </span>
          <button
            type="button"
            aria-label="Show fewer products"
            disabled={productCount <= 1}
            onClick={() => onProductCount(productCount - 1)}
            className="p-1.5 border border-solid border-collectors-black disabled:opacity-30
              disabled:cursor-not-allowed hover:bg-collectors-black hover:text-white
              transition-colors"
          >
            <Minus size={12} />
          </button>
          <span className="font-mono text-xs w-4 text-center">{productCount}</span>
          <button
            type="button"
            aria-label="Show more products"
            disabled={productCount >= maxProducts}
            onClick={() => onProductCount(productCount + 1)}
            className="p-1.5 border border-solid border-collectors-black disabled:opacity-30
              disabled:cursor-not-allowed hover:bg-collectors-black hover:text-white
              transition-colors"
          >
            <Plus size={12} />
          </button>
        </div>
        )}
      </div>

      {open && (
        <div className="border border-solid border-collectors-black p-4 flex flex-col gap-4">
          <div className="flex gap-2">
            <Chip active={false} onClick={onShowAll}>
              Show all
            </Chip>
            <Chip active={false} onClick={onHideAll}>
              Hide all
            </Chip>
          </div>

          {/* A fieldset per group: these really are sets of related toggles, and
              it gives each one an accessible name rather than a loose label. */}
          {groups.filter((g) => g.columns.some((c) => !c.locked)).map((group) => (
            <fieldset
              key={group.key}
              className="flex flex-wrap items-center gap-2 border-0 p-0 m-0"
            >
              <legend className="sr-only">{group.label}</legend>
              <span
                aria-hidden="true"
                className="font-mono text-[11px] uppercase tracking-widest text-collectors-gray w-32"
              >
                {group.label}
              </span>
              {group.columns
                .filter((c) => !c.locked)
                .map((col) => (
                  <Chip
                    key={col.key}
                    active={!hidden.has(col.key)}
                    onClick={() => onToggleColumn(col.key)}
                  >
                    {col.label}
                  </Chip>
                ))}
            </fieldset>
          ))}

          {hidden.size === hideableCount && (
            <p className="font-mono text-[11px] text-collectors-gray">
              Every optional column is hidden. The first column stays, because a row
              with no label is not a row.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
